import React, {useEffect, useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Typography,
    Checkbox,
    Chip,
    IconButton, 
    InputAdornment, 
    CircularProgress, 
    Snackbar, 
    Alert,
    Link,
    Tooltip
} from "@mui/material";
import {Add, CloudUpload, Delete, Search} from "@mui/icons-material";
import { formatDate } from "../../utils/dateUtils";
import API from "../../api/v1";

const SupplementaryKnowledgeDocument = ({
                                            documents = [],
                                            orgId,
                                            projectId,
                                            knowbaseId,
                                            total = 0,
                                            currentPage = 0,
                                            rowsPerPage: externalRowsPerPage = 10,
                                            onDocumentsUpdate,
                                            onRefreshData,
                                            onUpdateKnowledgeBase
}) => {
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [page, setPage] = useState(currentPage);
    const [rowsPerPage, setRowsPerPage] = useState(externalRowsPerPage);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [currentEditItem, setCurrentEditItem] = useState(null);
    const [isMultiDelete, setIsMultiDelete] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [downloading, setDownloading] = useState({}); // 追蹤下載狀態
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleAddClick = () => {
        setCurrentEditItem(null);
        setFileName('');
        setSelectedFile(null);
        setOpenAddDialog(true);
    };

    const handleUploadClick = (item) => {
        setCurrentEditItem(item);
        setFileName(item.fileName);
        setSelectedFile(null);
        setOpenAddDialog(true);
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setSelectedFile(null);
        setFileName('');
        setCurrentEditItem(null);
    };

    const handleDeleteDialog = (isMulti = false) => {
        setIsMultiDelete(isMulti);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        if (!isMultiDelete) {
            setCurrentEditItem(null);
        }
    };

    // 新增下載功能
    const handleDownload = async (documentId, fileName) => {
        if (!documentId || !fileName) return;

        setDownloading(prev => ({ ...prev, [documentId]: true }));
        try {
            const response = await API.downloadDocument(orgId, projectId, knowbaseId, documentId);

            // 創建一個 Blob 對象
            const blob = new Blob([response], {
                type: getFileFormat(fileName) === 'csv'
                    ? 'text/csv'
                    : 'text/plain'
            });

            // 創建下載鏈接
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);

            // 觸發下載
            document.body.appendChild(link);
            link.click();

            // 清理
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('下載文件時出錯:', error);
            setSnackbar({
                open: true,
                message: '下載文件時發生錯誤',
                severity: 'error'
            });
        } finally {
            setDownloading(prev => ({ ...prev, [documentId]: false }));
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true); // 顯示全屏 loading

            if (isMultiDelete) {
                // 批次刪除
                for (const id of selectedItems) {
                    await API.deleteDocument(
                        orgId,
                        projectId,
                        id
                    );
                }
                setSelectedItems([]); // 清空選擇項目
            } else {
                // 單一刪除
                await API.deleteDocument(
                    orgId,
                    projectId,
                    currentEditItem.id
                );
            }

            setSnackbar({
                open: true,
                message: '刪除成功',
                severity: 'success'
            });
            onRefreshData();
            handleCloseDeleteDialog();
            onUpdateKnowledgeBase();
        } catch (err) {
            console.error('刪除文件錯誤:', err);
            setSnackbar({
                open: true,
                message: '刪除失敗',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        onRefreshData(newPage + 1, rowsPerPage, searchKeyword); // API 是從 1 開始計算頁數
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        onRefreshData(1, newRowsPerPage, searchKeyword); // 切換每頁筆數時回到第一頁
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelected = documents.map((n) => n.id);  // 改用 documents
            setSelectedItems(newSelected);
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelect = (id) => {
        const selectedIndex = selectedItems.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedItems, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedItems.slice(1));
        } else if (selectedIndex === selectedItems.length - 1) {
            newSelected = newSelected.concat(selectedItems.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedItems.slice(0, selectedIndex),
                selectedItems.slice(selectedIndex + 1),
            );
        }

        setSelectedItems(newSelected);
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // 檢查是否為更新文件且檔案類型不同
            if (currentEditItem) {
                const newFileFormat = file.name.split('.').pop().toLowerCase();
                const currentFileFormat = currentEditItem.format.toLowerCase();

                if (newFileFormat !== currentFileFormat) {
                    setSnackbar({
                        open: true,
                        message: `檔案類型不符合，原檔案為 ${currentFileFormat.toUpperCase()} 格式，請上傳相同格式的檔案`,
                        severity: 'error'
                    });
                    // 清空已選擇的檔案
                    event.target.value = '';
                    return;
                }
            }
            setSelectedFile(file);
            if (!currentEditItem) {
                setFileName(file.name);
            }
        }
    };

    // 處理搜尋
    const handleSearch = () => {
        setPage(0); // 重置頁數到第一頁
        onRefreshData(1, rowsPerPage, searchKeyword); // 傳遞搜尋關鍵字
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('fileName', selectedFile.name);

            let response;
            if (currentEditItem) {
                try {
                    response = await API.updateDocument(
                        orgId,
                        projectId,
                        knowbaseId,
                        currentEditItem.id,
                        formData
                    );
                } catch (error) {
                    throw error;
                }
            } else {
                // 新增文件時需要額外的參數
                formData.append('category', 'supplementary');
                formData.append('type', 'suppl');  // 新增 type 參數
                formData.append('format', selectedFile.name.split('.').pop().toLowerCase());

                response = await API.uploadDocument(
                    orgId,
                    projectId,
                    knowbaseId,
                    formData
                );
            }

            if (response.code === 0) {
                setSnackbar({
                    open: true,
                    message: currentEditItem ? '更新成功' : '新增成功',
                    severity: 'success'
                });
                handleCloseAddDialog();
                onRefreshData();
                onUpdateKnowledgeBase();
            } else {
                throw new Error(response.message || (currentEditItem ? '更新失敗' : '新增失敗'));
            }
        } catch (err) {
            console.error('上傳文件錯誤:', err);
            setSnackbar({
                open: true,
                message: err.message,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const getFileFormat = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return extension === 'csv' ? 'csv' : 'txt';
    };

    useEffect(() => {
        setPage(currentPage);
        setRowsPerPage(externalRowsPerPage);
    }, [currentPage, externalRowsPerPage]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row'}}>
                    <TextField
                        placeholder='搜尋檔案名稱'
                        size='small'
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        sx={{width: '100%', maxWidth: '240px', mr: 2}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                    <Button
                        variant='contained'
                        size='medium'
                        onClick={handleSearch}
                        sx={{
                            flexShrink: '0',
                            minWidth: '47px',
                            mr: 1,
                            backgroundColor: '#2196F3',
                            '&:hover': { backgroundColor: '#1E88E5' }
                        }}
                    >搜尋</Button>
                </Box>
                <Button
                    startIcon={<Add />}
                    onClick={handleAddClick}
                    sx={{
                        color: '#2196F3',
                        '&:hover': { color: '#1E88E5' }
                    }}
                >
                    新增
                </Button>
            </Box>
            {loading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <TableContainer component={Paper}>
                <Table sx={{ width: '956px' }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                            <TableCell align='center' padding="checkbox" sx={{position: 'relative'}}>
                                <Checkbox
                                    indeterminate={selectedItems.length > 0 && selectedItems.length < documents.length}  // 改用 documents
                                    checked={documents.length > 0 && selectedItems.length === documents.length}  // 改用 documents
                                    onChange={handleSelectAll}
                                    sx={{height: '100%'}}
                                />
                                {selectedItems.length > 0 && (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setIsMultiDelete(true);
                                            setOpenDeleteDialog(true);
                                        }}
                                        sx={{ ml: 1 , position: 'absolute', right: '-32px', top: '50%', transform: 'translateY(-50%)' }}
                                    >
                                        <Delete />
                                    </IconButton>
                                )}
                            </TableCell>
                            <TableCell width={400} align="center">檔案名稱</TableCell>
                            <TableCell width={80} align="center">檔案類型</TableCell>
                            <TableCell width={200} align="center">上傳時間</TableCell>
                            <TableCell width={200} align="center">動作</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.map((row) => (
                                <TableRow
                                    key={row.id}
                                    sx={{
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                    }}
                                >
                                    <TableCell align="center" padding="checkbox">
                                        <Checkbox
                                            checked={selectedItems.indexOf(row.id) !== -1}
                                            onChange={() => handleSelect(row.id)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {downloading[row.id] ? (
                                            <Box display="flex" alignItems="center" justifyContent="center">
                                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                                下載中...
                                            </Box>
                                        ) : (
                                            <Tooltip title="點擊下載文件">
                                                <Link
                                                    component="button"
                                                    onClick={() => handleDownload(row.id, row.fileName)}
                                                    sx={{
                                                        color: 'rgb(0 0 0 / 87%)',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {row.fileName}
                                                </Link>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            variant="outlined"
                                            label={row.format.toUpperCase()}
                                            color={row.format === 'csv' ? 'success' : 'primary'}
                                            sx={{
                                                backgroundColor: row.format === 'csv'
                                                    ? 'rgba(42, 125, 50, 0.04)'
                                                    : 'rgba(33, 150, 243, 0.04)'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">{formatDate(row.updatedAt)}</TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleUploadClick(row)}
                                            sx={{
                                                minWidth: '47px',
                                                mr: 1,
                                                backgroundColor: '#2196F3',
                                                '&:hover': { backgroundColor: '#1E88E5' }
                                            }}
                                        >
                                            上傳
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => {
                                                setCurrentEditItem(row);
                                                setIsMultiDelete(false);
                                                setOpenDeleteDialog(true);
                                            }}
                                            sx={{
                                                minWidth: '47px',
                                                backgroundColor: '#F44336',
                                                '&:hover': { backgroundColor: '#C62828' }
                                            }}
                                        >
                                            刪除
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 30, 40, 50]}
                    component="div"
                    count={total}  // 使用 API 回傳的總筆數
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="每頁筆數："
                />
            </TableContainer>

            {/* 新增/上傳檔案對話框 */}
            <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {currentEditItem ? '上傳新版本' : '新增文件'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            檔案名稱：{currentEditItem ? currentEditItem.fileName : '等待選擇'}（支援格式：txt, csv）
                        </Typography>
                        <input
                            type="file"
                            accept=".txt,.csv"
                            style={{ display: 'none' }}
                            id="file-upload"
                            onChange={handleFileSelect}
                        />
                        <label htmlFor="file-upload">
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<CloudUpload />}
                                sx={{
                                    backgroundColor: '#2196F3',
                                    '&:hover': { backgroundColor: '#1E88E5' }
                                }}
                            >
                                瀏覽本地檔案
                            </Button>
                        </label>
                        {selectedFile && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                已選擇：{selectedFile.name}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog} sx={{ color: '#2196F3' }}>
                        取消
                    </Button>
                    <Button
                        onClick={handleUpload}
                        variant="contained"
                        disabled={!selectedFile || loading}
                        sx={{
                            backgroundColor: '#2196F3',
                            '&:hover': { backgroundColor: '#1E88E5' }
                        }}
                    >
                        {loading ? '處理中...' : (currentEditItem ? '更新' : '儲存')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 刪除確認對話框 */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>確認刪除</DialogTitle>
                <DialogContent>
                    <Typography>
                        {isMultiDelete
                            ? `確定要刪除這 ${selectedItems.length} 筆資料嗎？此動作無法復原。`
                            : '確定要刪除這筆資料嗎？此動作無法復原。'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} sx={{ color: '#2196F3' }}>
                        取消
                    </Button>
                    <Button onClick={handleDelete} sx={{ color: '#2196F3' }}>
                        刪除
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SupplementaryKnowledgeDocument;